import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { useCertificationRecordData } from './useCertificationRecordData';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import { MemberMissingCerts } from './useCertificationChecker';
import { CertificationRecord } from '../types/types';
import { Member, ProjectWithCertificationsQuery, ProjectWithCertificationsQueryVariables } from '__generated__/graphql';
import { PROJECT_WITH_CERTIFICATIONS_QUERY } from 'apollo/queries/project-queries';
import { getFullNameFromMember } from 'utils/stringUtils';
import { NonNullArrayField } from 'types/util/NonNullArrayField';

export type ProjectCertificationLink = NonNullArrayField<
  NonNullArrayField<ProjectWithCertificationsQuery['projects']>['certificationLinks']
>;

export function useProjectCertificationChecker() {
  const client = useGraphQLClient();
  const getCertificationRecords = useCertificationRecordData();

  const hasMissingProjectCertifications = useCallback(
    async (memberIds: string[], projectIds: string[]): Promise<boolean> => {
      if (projectIds.length === 0) {
        return false;
      }

      const certificationRecords: CertificationRecord[] = await getCertificationRecords(memberIds);

      const projectResult = await client.request<
        ProjectWithCertificationsQuery,
        ProjectWithCertificationsQueryVariables
      >({
        document: PROJECT_WITH_CERTIFICATIONS_QUERY,
        variables: {
          filter: {
            id: { contains: projectIds },
          },
        },
      });

      if (isNil(projectResult.projects) || projectResult.projects.length === 0) {
        throw new Error('Project not found');
      }

      const requiredCertIds: string[] = [];

      projectResult.projects.forEach((project) => {
        project.certificationLinks?.forEach((cert) => {
          if (cert?.certificationId && cert.certification?.name && isNil(cert.deletedOn)) {
            requiredCertIds.push(cert.certificationId);
          }
        });

        project.ancestors?.forEach((ancestor) => {
          ancestor.certificationLinks.forEach((cert) => {
            if (cert?.certificationId && cert.certification?.name && isNil(cert.deletedOn)) {
              requiredCertIds.push(cert.certificationId);
            }
          });
        }); // Ancestor projects' certifications also apply
      });

      const unqiueRequiredCertIds = Array.from(new Set(requiredCertIds));

      const recordsByMember: Map<string, string[]> = new Map();

      memberIds.forEach((memberId) => {
        recordsByMember.set(
          memberId,
          certificationRecords.filter((record) => record.memberId === memberId).map((record) => record.certificationId)
        );
      });

      return !Array.from(recordsByMember.values()).every((certIds) =>
        unqiueRequiredCertIds.every((reqId) => certIds.includes(reqId))
      );
    },
    [client, getCertificationRecords]
  );

  const getMissingProjectCertifications = useCallback(
    async (members: Member[], projectIds: string[]): Promise<MemberMissingCerts[]> => {
      if (projectIds.length === 0) {
        return [];
      }

      const memberIds = members.map((m) => m.id);
      const certificationRecords: CertificationRecord[] = await getCertificationRecords(memberIds);

      const projectResult = await client.request<
        ProjectWithCertificationsQuery,
        ProjectWithCertificationsQueryVariables
      >({
        document: PROJECT_WITH_CERTIFICATIONS_QUERY,
        variables: {
          filter: {
            id: { contains: projectIds },
          },
        },
      });

      if (isNil(projectResult.projects) || projectResult.projects.length === 0) {
        throw new Error('Project not found');
      }

      const requiredCertMap = new Map<string, string>();

      projectResult.projects.forEach((project) => {
        project.certificationLinks?.forEach((cert) => {
          if (
            cert?.certificationId &&
            cert.certification?.name &&
            isNil(cert.deletedOn) &&
            !requiredCertMap.has(cert.certificationId)
          ) {
            requiredCertMap.set(cert.certificationId, cert.certification.name);
          }
        });

        project.ancestors?.forEach((ancestor) => {
          ancestor.certificationLinks.forEach((cert) => {
            if (
              cert?.certificationId &&
              cert.certification?.name &&
              isNil(cert.deletedOn) &&
              !requiredCertMap.has(cert.certificationId)
            ) {
              requiredCertMap.set(cert.certificationId, cert.certification.name);
            }
          });
        }); // Ancestor projects' certifications also apply
      });

      const requiredCertIds = Array.from(requiredCertMap.keys());

      const recordsByMember: MemberMissingCerts[] = members.map((member) => {
        const memberCertIds = certificationRecords
          .filter((record) => record.memberId === member.id)
          .map((record) => record.certificationId);

        const missingCerts = requiredCertIds.filter((certId) => !memberCertIds.includes(certId));

        return {
          memberId: member.id,
          memberName: getFullNameFromMember(member),
          missingCerts: missingCerts.map((id) => requiredCertMap.get(id)!),
        };
      });

      return recordsByMember;
    },
    [client, getCertificationRecords]
  );

  return { hasMissingProjectCertifications, getMissingProjectCertifications };
}
